import { useState, useEffect, useMemo } from "react";

export default function ReviewDecisionPanel({
  assignment,
  stageDef,
  workflowDefinition,
  currentStageIds = [],
  outgoingConnections = [],
  projectHistory = [],
  onSubmit,
  onClose,
  disabled = false,
}) {
  const [selectedNextStageId, setSelectedNextStageId] = useState(null);
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skipWarning, setSkipWarning] = useState(null);
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);

  const stageMap = useMemo(() => {
    const map = new Map();
    (outgoingConnections || []).forEach((conn) => {
      map.set(conn.targetStageId, conn.targetStageName || conn.targetStageId);
    });
    return map;
  }, [outgoingConnections]);

  const isTerminalStage = outgoingConnections.length === 0;
  const isParallelStage =
    stageDef?.branchMode === "PARALLEL" && outgoingConnections.length >= 2;

  // Client-side mirror of the backend join rule, for display only: which
  // successors start on approval, and which keep waiting for other
  // predecessors that are still able to finish.
  const approvePreview = useMemo(() => {
    if (!isParallelStage || !workflowDefinition)
      return { starting: [], waiting: [] };
    const connections = workflowDefinition.connections || [];
    const stages = workflowDefinition.stages || [];
    const nameOf = (id) => stages.find((s) => s.id === id)?.name || id;
    const doneIds = new Set(
      (projectHistory || [])
        .filter((h) => h.status === "DONE")
        .map((h) => h.stageId),
    );
    const visitedIds = new Set(
      (projectHistory || [])
        .filter((h) => ["DONE", "RETURNED"].includes(h.status))
        .map((h) => h.stageId),
    );
    const activeIds = new Set([
      ...(currentStageIds || []),
      ...(projectHistory || [])
        .filter((h) =>
          ["PENDING", "IN_PROGRESS", "SUBMITTED"].includes(h.status),
        )
        .map((h) => h.stageId),
    ]);
    activeIds.delete(assignment?.stageId);
    doneIds.add(assignment?.stageId); // approving marks this stage done
    const reachable = new Set();
    const queue = [...activeIds];
    while (queue.length) {
      const node = queue.shift();
      if (reachable.has(node)) continue;
      reachable.add(node);
      connections
        .filter((c) => c.sourceStageId === node)
        .forEach((c) => queue.push(c.targetStageId));
    }
    const starting = [];
    const waiting = [];
    (outgoingConnections || []).forEach((conn) => {
      const target = conn.targetStageId;
      if (visitedIds.has(target) || activeIds.has(target)) return;
      const blockers = connections
        .filter((c) => c.targetStageId === target)
        .map((c) => c.sourceStageId)
        .filter(
          (p) => !doneIds.has(p) && (activeIds.has(p) || reachable.has(p)),
        );
      if (blockers.length) {
        waiting.push({ name: nameOf(target), blockers: blockers.map(nameOf) });
      } else {
        starting.push({ id: target, name: nameOf(target) });
      }
    });
    return { starting, waiting };
  }, [
    isParallelStage,
    workflowDefinition,
    projectHistory,
    currentStageIds,
    outgoingConnections,
    assignment?.stageId,
  ]);

  // Branches default to all-selected, which reproduces the original
  // "approve starts everything" behaviour unless the reviewer opts out.
  const startableKey = approvePreview.starting.map((s) => s.id).join("|");
  useEffect(() => {
    setSelectedTargetIds(startableKey ? startableKey.split("|") : []);
  }, [startableKey]);

  const toggleTarget = (targetId) => {
    setSelectedTargetIds((current) =>
      current.includes(targetId)
        ? current.filter((id) => id !== targetId)
        : [...current, targetId],
    );
  };

  const unselectedBranches = approvePreview.starting.filter(
    (s) => !selectedTargetIds.includes(s.id),
  );

  const isReturnConnection = (targetStageId) =>
    projectHistory.some(
      (h) =>
        h.stageId === targetStageId && ["DONE", "RETURNED"].includes(h.status),
    );

  const requiresNote =
    decision === "REWORK" ||
    (decision === "MOVE" &&
      selectedNextStageId &&
      isReturnConnection(selectedNextStageId));

  const confirmDisabled =
    !decision ||
    disabled ||
    isSubmitting ||
    (requiresNote && !note.trim()) ||
    (decision === "APPROVE" &&
      approvePreview.starting.length > 0 &&
      selectedTargetIds.length === 0);

  const isReturnMove =
    decision === "MOVE" &&
    selectedNextStageId &&
    isReturnConnection(selectedNextStageId);

  const confirmLabel = isSubmitting
    ? "Submitting..."
    : isReturnMove
      ? "Send Back →"
      : {
          MOVE: "Move Forward →",
          APPROVE: "Approve →",
          REWORK: "Request Rework →",
          COMPLETE: "Complete Project ✓",
        }[decision] || "Confirm";

  const bypassedAssignments = useMemo(() => {
    if (!Array.isArray(skipWarning?.bypassedAssignments)) return [];
    return skipWarning.bypassedAssignments;
  }, [skipWarning]);

  const bypassedStageSummary = useMemo(() => {
    if (!bypassedAssignments.length) return [];
    const byStage = new Map();
    bypassedAssignments.forEach((item) => {
      const key = item.stageId || item.stageName || item.id;
      if (!byStage.has(key)) {
        byStage.set(key, {
          stageId: item.stageId || null,
          stageName: item.stageName || item.stageId || "Unknown stage",
          assignees: [],
        });
      }
      const group = byStage.get(key);
      if (item.assigneeName) group.assignees.push(item.assigneeName);
    });
    return Array.from(byStage.values());
  }, [bypassedAssignments]);

  const optionStyles = {
    MOVE: { color: "#15803D", bg: "#F0FDF4" },
    RETURN: { color: "#B45309", bg: "#FFFBEB" },
    REWORK: { color: "#DC2626", bg: "#FEF2F2" },
    COMPLETE: { color: "#4F46E5", bg: "#EEF2FF" },
  };

  const getCardStyle = (type, isSelected) => {
    const tone = optionStyles[type];
    return {
      border: `1.5px solid ${isSelected ? tone.color : "#E5E7EB"}`,
      background: isSelected ? `${tone.bg}` : "#FFFFFF",
      borderRadius: 10,
      padding: "14px 16px",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      transition: "all 0.15s",
      boxShadow: isSelected
        ? `0 0 0 3px ${tone.color}26`
        : "0 0 0 0 transparent",
      opacity: disabled ? 0.6 : 1,
      textAlign: "left",
      width: "100%",
      backgroundClip: "padding-box",
    };
  };

  const getIconStyle = (type) => {
    const tone = optionStyles[type];
    return {
      width: 36,
      height: 36,
      borderRadius: 8,
      background: `${tone.color}1F`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
      flexShrink: 0,
      color: tone.color,
    };
  };

  const optionTitleStyle = {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  };

  const optionDescStyle = {
    fontSize: 13,
    color: "#6B7280",
    marginTop: 2,
  };

  const branchPickerStyle = {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 10,
    background: "#F9FAFB",
  };

  const branchPickerTitleStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 8,
  };

  const branchRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#111827",
    padding: "4px 0",
    cursor: disabled ? "not-allowed" : "pointer",
  };

  const branchWarningStyle = {
    margin: "8px 0 0",
    fontSize: 12,
    color: "#B45309",
    lineHeight: 1.5,
  };

  const handleSubmit = async (confirmSkip = false) => {
    if (requiresNote && !note.trim()) {
      setNoteError("Please add a note");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onSubmit({
        decision,
        nextStageId: selectedNextStageId,
        note,
        confirmSkip,
        ...(decision === "APPROVE" && approvePreview.starting.length > 0
          ? { selectedTargetIds }
          : {}),
      });

      if (result?.code === "CONFIRM_SKIP_REQUIRED") {
        setSkipWarning(result);
        setIsSubmitting(false);
        return;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (skipWarning) {
    return (
      <div className="review-panel">
        <div
          style={{
            background: "#FEF2F2",
            border: "1.5px solid #FECACA",
            borderRadius: 12,
            padding: 20,
          }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#FECACA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#B91C1C",
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              ⚠
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 600, color: "#991B1B" }}>
                Stages will be skipped
              </p>
              <p style={{ margin: "4px 0 0", color: "#7F1D1D", fontSize: 13 }}>
                Moving forward will skip {bypassedStageSummary.length} stage
                {bypassedStageSummary.length === 1 ? "" : "s"} and mark related
                active assignments as SKIPPED:
              </p>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            {bypassedStageSummary.map((stage) => (
              <div
                key={stage.stageId || stage.stageName}
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #FECACA",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <span style={{ fontWeight: 600, color: "#7F1D1D" }}>
                  {stage.stageName}
                </span>
                <span style={{ fontSize: 12, color: "#991B1B" }}>
                  Status after confirmation: SKIPPED
                </span>
                {stage.assignees.length > 0 && (
                  <span style={{ fontSize: 12, color: "#991B1B" }}>
                    Active assignee
                    {stage.assignees.length === 1 ? "" : "s"}:{" "}
                    {stage.assignees.join(", ")}
                  </span>
                )}
              </div>
            ))}
          </div>

          <p style={{ margin: "12px 0 0", color: "#7F1D1D", fontSize: 13 }}>
            This cannot be undone.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 16,
            }}
          >
            <button
              className="btn-secondary"
              onClick={() => setSkipWarning(null)}
            >
              Go Back
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                setSkipWarning(null);
                handleSubmit(true);
              }}
              disabled={isSubmitting}
            >
              Skip and Continue →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="review-panel">
      <h3 style={{ marginTop: 12, marginBottom: 12 }}>Review Decision</h3>

      <h4 className="review-question">What should happen next?</h4>

      {isTerminalStage ? (
        <>
          <div className="terminal-notice">🏁 This is the final stage</div>

          <button
            type="button"
            style={{
              ...getCardStyle("COMPLETE", decision === "COMPLETE"),
              marginBottom: 10,
            }}
            onClick={() => {
              setDecision("COMPLETE");
              setSelectedNextStageId(null);
              setNoteError("");
            }}
            disabled={disabled}
          >
            <div style={getIconStyle("COMPLETE")}>✓</div>
            <div>
              <div style={optionTitleStyle}>Mark project as complete</div>
              <div style={optionDescStyle}>
                All stages are done. This will close the project.
              </div>
            </div>
          </button>

          <button
            type="button"
            style={getCardStyle("REWORK", decision === "REWORK")}
            onClick={() => {
              setDecision("REWORK");
              setSelectedNextStageId(null);
              setNoteError("");
            }}
            disabled={disabled}
          >
            <div style={getIconStyle("REWORK")}>↺</div>
            <div>
              <div style={optionTitleStyle}>
                Needs rework — redo {assignment?.stageName}
              </div>
              <div style={optionDescStyle}>
                The submission has issues. The assignee will redo this stage.
              </div>
            </div>
          </button>
        </>
      ) : isParallelStage ? (
        <>
          <button
            type="button"
            style={{
              ...getCardStyle("MOVE", decision === "APPROVE"),
              marginBottom: 10,
            }}
            onClick={() => {
              setDecision("APPROVE");
              setSelectedNextStageId(null);
              setNote("");
              setNoteError("");
            }}
            disabled={disabled}
          >
            <div style={getIconStyle("MOVE")}>✓</div>
            <div>
              <div style={optionTitleStyle}>
                {approvePreview.starting.length === 0
                  ? "Approve"
                  : unselectedBranches.length === 0
                    ? "Approve — all branches start"
                    : `Approve — start ${selectedTargetIds.length} of ${approvePreview.starting.length} branches`}
              </div>
              <div style={optionDescStyle}>
                {approvePreview.starting.length > 0 && (
                  <>Choose which branches start now. </>
                )}
                {approvePreview.waiting.map((w) => (
                  <span key={w.name}>
                    {w.name} waits for {w.blockers.join(", ")}.{" "}
                  </span>
                ))}
                {approvePreview.starting.length === 0 &&
                  approvePreview.waiting.length === 0 && (
                    <>Work on this stage is complete.</>
                  )}
              </div>
            </div>
          </button>

          {decision === "APPROVE" && approvePreview.starting.length > 0 && (
            <div style={branchPickerStyle}>
              <div style={branchPickerTitleStyle}>
                Which branches start now?
              </div>
              {approvePreview.starting.map((branch) => (
                <label key={branch.id} style={branchRowStyle}>
                  <input
                    type="checkbox"
                    checked={selectedTargetIds.includes(branch.id)}
                    onChange={() => toggleTarget(branch.id)}
                    disabled={disabled}
                  />
                  <span>{branch.name}</span>
                </label>
              ))}

              {unselectedBranches.length > 0 && (
                <p style={branchWarningStyle}>
                  ⚠ {unselectedBranches.map((b) => b.name).join(", ")} will not
                  start. There is no "start later" yet — an unselected branch
                  only ever runs if another stage leads into it.
                </p>
              )}

              {selectedTargetIds.length === 0 && (
                <p style={branchWarningStyle}>
                  Select at least one branch to approve.
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            style={getCardStyle("REWORK", decision === "REWORK")}
            onClick={() => {
              setDecision("REWORK");
              setSelectedNextStageId(null);
              setNoteError("");
            }}
            disabled={disabled}
          >
            <div style={getIconStyle("REWORK")}>↺</div>
            <div>
              <div style={optionTitleStyle}>
                Needs rework — redo {assignment?.stageName}
              </div>
              <div style={optionDescStyle}>
                The submission has issues. The assignee will redo this stage.
              </div>
            </div>
          </button>
        </>
      ) : (
        <>
          {(outgoingConnections || [])
            .filter((conn) => !isReturnConnection(conn.targetStageId))
            .map((connection) => (
              <button
                type="button"
                key={connection.id}
                style={{
                  ...getCardStyle(
                    "MOVE",
                    decision === "MOVE" &&
                      selectedNextStageId === connection.targetStageId,
                  ),
                  marginBottom: 10,
                }}
                onClick={() => {
                  setDecision("MOVE");
                  setSelectedNextStageId(connection.targetStageId);
                  setNote("");
                  setNoteError("");
                }}
                disabled={disabled}
              >
                <div style={getIconStyle("MOVE")}>→</div>
                <div>
                  <div style={optionTitleStyle}>
                    Move to {connection.targetStageName}
                  </div>
                  <div style={optionDescStyle}>
                    Work on this stage is complete. Move the project forward.
                  </div>
                </div>
              </button>
            ))}

          {(outgoingConnections || [])
            .filter((conn) => isReturnConnection(conn.targetStageId))
            .map((connection) => (
              <button
                type="button"
                key={connection.id}
                style={{
                  ...getCardStyle(
                    "RETURN",
                    decision === "MOVE" &&
                      selectedNextStageId === connection.targetStageId,
                  ),
                  marginBottom: 10,
                }}
                onClick={() => {
                  setDecision("MOVE");
                  setSelectedNextStageId(connection.targetStageId);
                  setNoteError("");
                }}
                disabled={disabled}
              >
                <div style={getIconStyle("RETURN")}>↩</div>
                <div>
                  <div style={optionTitleStyle}>
                    Send back to {connection.targetStageName}
                  </div>
                  <div style={optionDescStyle}>
                    This stage needs to revisit an earlier stage before
                    continuing.
                  </div>
                </div>
              </button>
            ))}

          <button
            type="button"
            style={getCardStyle("REWORK", decision === "REWORK")}
            onClick={() => {
              setDecision("REWORK");
              setSelectedNextStageId(null);
              setNoteError("");
            }}
            disabled={disabled}
          >
            <div style={getIconStyle("REWORK")}>↺</div>
            <div>
              <div style={optionTitleStyle}>
                Needs rework — redo {assignment?.stageName}
              </div>
              <div style={optionDescStyle}>
                The submission has issues. The assignee will redo this stage.
              </div>
            </div>
          </button>
        </>
      )}

      {requiresNote && (
        <div
          style={{
            marginTop: 12,
            padding: "14px 16px",
            background: "#FFFBEB",
            border: "1.5px solid #FCD34D",
            borderRadius: 10,
          }}
        >
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#92400E",
              display: "block",
              marginBottom: 8,
            }}
          >
            {decision === "REWORK"
              ? "What needs to be fixed?"
              : "Why is this going back?"}
          </label>
          <textarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setNoteError("");
            }}
            placeholder="Add your note here..."
            rows={3}
            style={{
              width: "100%",
              border: "1px solid #FCD34D",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 13,
              resize: "vertical",
              background: "#FFFFFF",
              boxSizing: "border-box",
            }}
          />
          {noteError && (
            <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4 }}>
              {noteError}
            </p>
          )}
        </div>
      )}

      <span style={{ height: 10 }}></span>

      <div className="review-actions">
        <button
          className="btn-secondary"
          onClick={onClose}
          disabled={disabled || isSubmitting}
        >
          Cancel
        </button>
        <span style={{ width: 10 }}></span>
        <button
          className="btn-primary"
          onClick={() => handleSubmit(false)}
          disabled={confirmDisabled}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
